import React from 'react';
import type { TeamSecretActivityEvent } from '../client';
import MemberName from '../components/MemberName';

export interface SecretActivityMember {
  id: number;
  name: string;
}

export interface SecretActivityAgent {
  id: number;
  name?: string;
}

interface SecretActivityProps {
  teamId?: string;
  events: TeamSecretActivityEvent[];
  members?: SecretActivityMember[];
  agents?: SecretActivityAgent[];
  nextCursor?: string | null;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}

const label = (action: string) =>
  action.replace(/_/g, ' ').replace(/^./, (first) => first.toUpperCase());

const SecretActivity: React.FC<SecretActivityProps> = ({
  teamId = '',
  events,
  members = [],
  agents = [],
  nextCursor,
  loadingMore,
  onLoadMore,
}) => {
  const actorIdentity = (event: TeamSecretActivityEvent) => {
    if (event.actor.kind === 'human') {
      const userId = event.actor.userId;
      const member = members.find((candidate) => candidate.id === userId);
      return (
        <MemberName teamId={teamId} userId={userId}>
          {member?.name || `Former member #${userId || 'unknown'}`}
        </MemberName>
      );
    }
    if (event.actor.kind === 'agent') {
      const agentId = event.actor.agentId;
      const agent = agents.find((candidate) => candidate.id === agentId);
      return (
        <MemberName teamId={teamId} agentId={agentId}>
          {agent?.name || `Deleted agent #${agentId || 'unknown'}`}
        </MemberName>
      );
    }
    return event.actor.kind.replace(/^./, (first) => first.toUpperCase());
  };

  const activityDescription = (event: TeamSecretActivityEvent) => {
    const targetAgentId =
      event.afterMetadata?.agentId ?? event.beforeMetadata?.agentId;
    if (
      (event.action === 'grant' || event.action === 'ungrant') &&
      targetAgentId
    ) {
      const target = agents.find((candidate) => candidate.id === targetAgentId);
      return (
        <>
          {actorIdentity(event)}{' '}
          {event.action === 'grant' ? 'granted' : 'removed'}{' '}
          <MemberName teamId={teamId} agentId={targetAgentId}>
            {target?.name || `Deleted agent #${targetAgentId}`}
          </MemberName>{' '}
          access
        </>
      );
    }
    return (
      <>
        {actorIdentity(event)} · {event.origin}
      </>
    );
  };

  return (
    <section aria-label="Secret activity">
      {!events.length ? (
        <p className="secret-state">No activity has been recorded.</p>
      ) : (
        <ol className="secret-activity-list">
          {events.map((event) => (
            <li key={event.id}>
              <span className="secret-activity-mark" aria-hidden="true" />
              <div>
                <strong>{label(event.action)}</strong>
                <span>{activityDescription(event)}</span>
              </div>
              <time dateTime={event.createdAt}>
                {new Date(event.createdAt).toLocaleString()}
              </time>
            </li>
          ))}
        </ol>
      )}
      {nextCursor && onLoadMore && (
        <button
          type="button"
          className="lightButton secret-load-more"
          disabled={loadingMore}
          onClick={onLoadMore}>
          {loadingMore ? 'Loading…' : 'Load more activity'}
        </button>
      )}
    </section>
  );
};

export default SecretActivity;
